import { FormGroup } from '@angular/forms'
import { DateTools } from './date-tools'

export type TimeSlotType = 'morning' | 'afternoon' | 'custom'

/**
 * Classe utilitaire pour gérer les créneaux horaires
 */
export class TimeSlotTools {
  /**
   * Sélectionne un créneau horaire prédéfini et met à jour le formulaire
   * @param form Le formulaire à mettre à jour
   * @param slot Le type de créneau horaire à sélectionner
   * @returns Le créneau horaire sélectionné
   */
  public static selectTimeSlot(
    form: FormGroup,
    slot: TimeSlotType
  ): TimeSlotType {
    const startTimeSlot = new Date()
    const endTimeSlot = new Date()

    // Réinitialiser les heures, minutes et secondes
    startTimeSlot.setHours(0, 0, 0, 0)
    endTimeSlot.setHours(0, 0, 0, 0)

    // Définir les heures en fonction du créneau sélectionné
    if (slot === 'morning') {
      // Matin: 8h-12h
      startTimeSlot.setHours(8, 0, 0)
      endTimeSlot.setHours(12, 0, 0)
    } else if (slot === 'afternoon') {
      // Après-midi: 13h-18h
      startTimeSlot.setHours(13, 0, 0)
      endTimeSlot.setHours(18, 0, 0)
    }

    // Mettre à jour le formulaire (seulement pour morning et afternoon)
    if (slot !== 'custom') {
      form.patchValue({
        startTimeSlot: startTimeSlot,
        endTimeSlot: endTimeSlot,
      })
    }

    return slot
  }

  /**
   * Détermine le type de créneau horaire en fonction des heures de début et de fin
   */
  public static determineTimeSlotType(
    startTime: any,
    endTime: any
  ): TimeSlotType {
    if (!startTime || !endTime) {
      return 'custom'
    }

    let startHour: number, endHour: number

    if (startTime instanceof Date && endTime instanceof Date) {
      startHour = startTime.getHours()
      endHour = endTime.getHours()
    } else if (typeof startTime === 'string' && typeof endTime === 'string') {
      startHour = parseInt(startTime.split(':')[0], 10)
      endHour = parseInt(endTime.split(':')[0], 10)
    } else {
      return 'custom'
    }

    // Vérifier si correspond au créneau du matin (8h-12h)
    if (startHour === 8 && endHour === 12) {
      return 'morning'
    }
    // Vérifier si correspond au créneau de l'après-midi (13h-18h)
    else if (startHour === 13 && endHour === 18) {
      return 'afternoon'
    } else {
      return 'custom'
    }
  }

  /**
   * Vérifie si le créneau horaire est cohérent avec la date sélectionnée
   * @param form Le formulaire contenant les données
   * @param selectedSlot Le type de créneau actuellement sélectionné
   * @returns true si un problème de cohérence est détecté
   */
  public static checkTimeSlotConsistency(
    form: FormGroup,
    selectedSlot: TimeSlotType
  ): boolean {
    const startDate = form.get('startDate')?.value
    const startTimeSlot = form.get('startTimeSlot')?.value
    const endTimeSlot = form.get('endTimeSlot')?.value

    if (!startDate || !startTimeSlot || !endTimeSlot) {
      return false // Pas assez d'information pour déterminer une incohérence
    }

    // Extraire les heures
    let startDateHour: number = 0
    let startSlotHour: number = 0
    let endSlotHour: number = 0

    if (startDate instanceof Date) {
      startDateHour = startDate.getHours()
    }

    if (startTimeSlot instanceof Date) {
      startSlotHour = startTimeSlot.getHours()
    } else if (typeof startTimeSlot === 'string') {
      startSlotHour = parseInt(startTimeSlot.split(':')[0], 10)
    }

    if (endTimeSlot instanceof Date) {
      endSlotHour = endTimeSlot.getHours()
    } else if (typeof endTimeSlot === 'string') {
      endSlotHour = parseInt(endTimeSlot.split(':')[0], 10)
    }

    // Vérifier si l'heure de startDate est comprise dans le créneau
    let mismatch = startDateHour < startSlotHour || startDateHour >= endSlotHour

    // Si un créneau spécifique est sélectionné, vérifier sa cohérence
    if (selectedSlot !== 'custom') {
      const isValidMorning =
        selectedSlot === 'morning' && startSlotHour === 8 && endSlotHour === 12

      const isValidAfternoon =
        selectedSlot === 'afternoon' &&
        startSlotHour === 13 &&
        endSlotHour === 18

      if (!isValidMorning && !isValidAfternoon) {
        mismatch = true
      }
    }

    return mismatch
  }

  /**
   * Met à jour le créneau horaire pour correspondre à la date sélectionnée
   * @param form Le formulaire à mettre à jour
   * @param currentSlot Le créneau actuel
   * @returns Le nouveau créneau sélectionné
   */
  public static updateTimeSlotToMatchDate(
    form: FormGroup,
    currentSlot: TimeSlotType
  ): TimeSlotType {
    const startDate = form.get('startDate')?.value
    if (!startDate || !(startDate instanceof Date)) {
      return currentSlot
    }

    const currentHour = startDate.getHours()
    let newSlot: TimeSlotType

    // Déterminer le créneau approprié en fonction de l'heure
    if (currentHour >= 8 && currentHour < 12) {
      newSlot = 'morning'
    } else if (currentHour >= 13 && currentHour < 18) {
      newSlot = 'afternoon'
    } else {
      // Si l'heure ne correspond à aucun créneau, utiliser le créneau actuel
      // ou par défaut le matin si le créneau est 'custom'
      newSlot = currentSlot !== 'custom' ? currentSlot : 'morning'
    }

    return this.selectTimeSlot(form, newSlot)
  }

  /**
   * Formate une heure au format "HH:MM:SS"
   * @param time L'heure à formater (Date ou string)
   * @returns L'heure formatée en chaîne
   */
  public static formatTimeForAPI(time: Date | string | null): string {
    if (!time) return '00:00:00'

    if (time instanceof Date) {
      return DateTools.formatTimeOnly(time)
    }

    // Si c'est déjà une chaîne avec le bon format, la retourner
    if (typeof time === 'string' && time.includes(':')) {
      const parts = time.split(':')
      const hours = parts[0].padStart(2, '0')
      const minutes = (parts[1] || '0').padStart(2, '0')
      const seconds = (parts[2] || '0').padStart(2, '0')
      return `${hours}:${minutes}:${seconds}`
    }

    return '00:00:00'
  }
}
